import { Tooltip, centsToUnits } from '@systemeio/ui'
import { PaymentTypesEnum } from 'modules/payment/transactions/enums/payment-types-enum'
import { SubscriptionIntervalTitlesIcu } from 'modules/payment/transactions/enums/subscription-intervals-enum'
import React from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { currencyFormat } from 'shared/utils/currency-format'
import { PricePlanType } from '@systemeio/types'

interface PaymentTooltipProps {
  pricePlan: PricePlanType | null
  children: React.ReactNode
  productPriceAmount?: string
  customRow?: React.ReactNode
  hiddenInfo?: boolean
  stayOnHover?: boolean
}

const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <p className="text-start">
    <span className="text-darkblue font-bold">{label}: </span>
    {children}
  </p>
)

export const PricePlanTooltip = ({
  pricePlan,
  children,
  hiddenInfo,
  customRow,
  stayOnHover,
  productPriceAmount,
}: PaymentTooltipProps) => {
  const { t, tICU } = useLocoTranslation()
  const { user } = useUser()

  if (!pricePlan) {
    return <div>{children}</div>
  }

  const { innerName, name, statementDescriptor, type, recurringOptions, amount, currency } =
    pricePlan
  const formattedAmount = currencyFormat(centsToUnits(amount), currency, user?.dashboardLocale)

  return (
    <Tooltip
      mode="hover"
      stayOnHover={stayOnHover}
      className="w-full max-w-fit"
      wrapperClassName="w-full"
      label={
        <div className="flex max-w-sm flex-col items-start gap-2">
          {!hiddenInfo ? (
            <InfoRow label={t('dashboard.price_plan.name')}>{name}</InfoRow>
          ) : (
            <InfoRow label={t('dashboard.price_plan.inner_name')}>{innerName}</InfoRow>
          )}

          {statementDescriptor && !hiddenInfo && (
            <InfoRow label={t('dashboard.price_plan.statement_descriptor')}>
              {statementDescriptor}
            </InfoRow>
          )}

          {!hiddenInfo && <InfoRow label={t('global.type')}>{t(PaymentTypesEnum[type])}</InfoRow>}

          {!recurringOptions && (
            <InfoRow label={t('dashboard.price_plan.payment_amount')}>
              {productPriceAmount ? productPriceAmount : formattedAmount}
            </InfoRow>
          )}

          {!!recurringOptions?.trialPeriod && (
            <InfoRow label={t('dashboard.price_plan.trial_period')}>
              {`${recurringOptions.trialPeriod} ${tICU(
                SubscriptionIntervalTitlesIcu[pricePlan.recurringOptions.trialInterval],
                {
                  count: pricePlan.recurringOptions.trialPeriod,
                },
              )}`}
            </InfoRow>
          )}

          {recurringOptions?.limitOfPayments && (
            <InfoRow label={t('dashboard.price_plan.limit_of_payments')}>
              {recurringOptions.limitOfPayments}
            </InfoRow>
          )}

          {recurringOptions && (
            <InfoRow label={t('dashboard.price_plan.payment_amount')}>
              {`${formattedAmount} ${t('global.each').toLowerCase()} ${
                recurringOptions.intervalCount
              } ${recurringOptions.interval}`}
            </InfoRow>
          )}

          {customRow}
        </div>
      }
    >
      {children}
    </Tooltip>
  )
}
